/**
 * DropPreview — "what happens to my wallet if I drop this person" block for
 * the drop/remove confirm dialogs (2026-09-03 owner request).
 *
 * Shows refund → cash after, the fee you'd owe on your NEXT add, the cash
 * left to spend, and the cheapest eligible refill for that slot type — so
 * nobody hits the backend strand safeguard by surprise. Turns red when the
 * drop would be blocked. Data: GET /teams/{id}/drop-preview.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Wallet } from 'lucide-react';
import { teamAPI } from '../lib/api';
import { formatFee } from '../lib/rosterMoveMath';

const f = (v) => `₣${formatFee(v)}`;
const typeLabel = (t) => (t || 'person').replace(/_/g, ' ');

export default function DropPreview({ teamId, entityId }) {
  const [p, setP] = useState(null);

  useEffect(() => {
    if (!teamId || !entityId) { setP(null); return undefined; }
    let alive = true;
    teamAPI.dropPreview(teamId, entityId)
      .then((r) => { if (alive) setP(r.data || null); })
      .catch(() => { if (alive) setP(null); });
    return () => { alive = false; };
  }, [teamId, entityId]);

  if (!p || p.deceased) return null;
  const blocked = !p.can_refill;
  const pct = Math.round((p.next_add_rate || 0) * 1000) / 10;
  return (
    <div
      className={`mt-3 rounded-xl border px-3 py-2 text-left text-xs ${
        blocked ? 'border-destructive/40 bg-destructive/10' : 'border-border/60 bg-secondary/40'
      }`}
      data-testid="drop-preview"
      data-can-refill={blocked ? '0' : '1'}
    >
      <div className="flex items-center gap-1.5 font-semibold mb-1.5">
        <Wallet className="h-3.5 w-3.5" /> After this drop
      </div>
      <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 tabular-nums">
        <dt className="text-muted-foreground">Refund</dt>
        <dd className="text-right" data-testid="drop-preview-refund">+{f(p.refund)}</dd>
        <dt className="text-muted-foreground">Cash</dt>
        <dd className="text-right" data-testid="drop-preview-cash">{f(p.cash_after)}</dd>
        {p.next_add_fee > 0 && (
          <>
            <dt className="text-muted-foreground">Next add fee ({pct}%)</dt>
            <dd className="text-right text-destructive" data-testid="drop-preview-fee">−{f(p.next_add_fee)}</dd>
          </>
        )}
        <dt className="text-muted-foreground font-medium">Left to refill</dt>
        <dd className="text-right font-semibold" data-testid="drop-preview-left">{f(p.cash_for_refill)}</dd>
        {p.cheapest && (
          <>
            <dt className="text-muted-foreground">Cheapest {typeLabel(p.cheapest.entity_type)}</dt>
            <dd className="text-right truncate max-w-[160px]" data-testid="drop-preview-cheapest">
              {p.cheapest.name} · {f(p.cheapest.price)}
            </dd>
          </>
        )}
      </dl>
      {blocked && (
        <p className="mt-2 flex items-start gap-1.5 font-semibold text-destructive" data-testid="drop-preview-blocked">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          You couldn't afford a replacement (short {f(p.shortfall)}) — this drop will be blocked.
        </p>
      )}
    </div>
  );
}
